/**
 * StockRebalancer.jsx - Smart Stock Rebalancer Dashboard
 * 
 * Refactored for maintainability - component dialogs extracted to ./rebalancer/
 * 
 * Effective Threshold Priority (3-tier hierarchy):
 * 1. Shop-Specific Override (highest) - per product + shop
 * 2. Product Custom Threshold - per shop type (Normal/Warehouse)
 * 3. Shop-Type Default (lowest) - global defaults
 */
import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Search,
  Settings,
  RefreshCw,
  RefreshCcw,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Package,
  Store,
  X,
  Plus,
  Trash2,
  Filter,
  BarChart3,
  Lightbulb,
  Warehouse,
  Building2,
  Sliders,
} from "lucide-react";

// Import extracted components
import {
  STATUS_STYLES,
  STATUS_LABELS,
  SHOP_TYPE_STYLES,
  StatsCards,
  UnifiedThresholdDialog,
  ThresholdDialog,
  ShopOverrideDialog,
  CrossShopView,
  RecommendationsView,
} from "./rebalancer";

export default function StockRebalancer({ shops = [], onCreateTransfer }) {
  const { user } = useAuth();
  const isMasterOrSuper = user?.role === "master" || user?.role === "super_user";

  // View mode: "dashboard" | "thresholds" | "recommendations" | "settings"
  const [viewMode, setViewMode] = useState("dashboard");

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Default threshold settings
  const [defaultSettings, setDefaultSettings] = useState({
    normal_min_qty: 2,
    normal_max_qty: 10,
    warehouse_min_qty: 5,
    warehouse_max_qty: 30
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [editedSettings, setEditedSettings] = useState(null);
  
  // Shops with types
  const [shopsWithTypes, setShopsWithTypes] = useState([]);

  // Cross-shop view
  const [crossShopItems, setCrossShopItems] = useState([]);
  const [crossShopTotal, setCrossShopTotal] = useState(0);
  const [crossShopLoading, setCrossShopLoading] = useState(false);
  const [crossShopPage, setCrossShopPage] = useState(1);
  const [crossShopSearch, setCrossShopSearch] = useState("");
  const [crossShopBrand, setCrossShopBrand] = useState("");
  const [crossShopGroup, setCrossShopGroup] = useState("");
  const [crossShopStatus, setCrossShopStatus] = useState("");
  const [crossShopCustomOnly, setCrossShopCustomOnly] = useState(false);

  // Recommendations
  const [recommendations, setRecommendations] = useState([]);
  const [recSummary, setRecSummary] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  
  // Recommendation filters (Phase 2D + shop filters)
  const [recSearch, setRecSearch] = useState("");
  const [recBrand, setRecBrand] = useState("");
  const [recGroup, setRecGroup] = useState("");
  const [recFromShop, setRecFromShop] = useState(""); // FROM shop filter
  const [recToShop, setRecToShop] = useState("");     // TO shop filter
  const [recFilteredCount, setRecFilteredCount] = useState(0);
  const [recTotalCount, setRecTotalCount] = useState(0); // Unfiltered total for "X of Y"
  const [recFilteredSummary, setRecFilteredSummary] = useState(null); // Summary for filtered results

  // Threshold management
  const [thresholds, setThresholds] = useState([]);
  const [thresholdsTotal, setThresholdsTotal] = useState(0);
  const [thresholdsLoading, setThresholdsLoading] = useState(false);
  const [thresholdSearch, setThresholdSearch] = useState("");

  // Threshold dialog
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [thresholdDialogMode, setThresholdDialogMode] = useState("create"); // "create" | "edit"
  const [thresholdProduct, setThresholdProduct] = useState(null);
  // Shop-type specific threshold values
  const [normalMinQty, setNormalMinQty] = useState("");
  const [normalMaxQty, setNormalMaxQty] = useState("");
  const [warehouseMinQty, setWarehouseMinQty] = useState("");
  const [warehouseMaxQty, setWarehouseMaxQty] = useState("");
  const [thresholdSaving, setThresholdSaving] = useState(false);

  // Product search for threshold creation
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearching, setProductSearching] = useState(false);

  // Shop Override Dialog (Phase 2E)
  const [shopOverrideDialogOpen, setShopOverrideDialogOpen] = useState(false);
  const [shopOverrideProduct, setShopOverrideProduct] = useState(null);
  const [shopOverrideShop, setShopOverrideShop] = useState(null);
  const [shopOverrideMinQty, setShopOverrideMinQty] = useState("");
  const [shopOverrideMaxQty, setShopOverrideMaxQty] = useState("");
  const [shopOverrideSaving, setShopOverrideSaving] = useState(false);
  const [shopOverrideExisting, setShopOverrideExisting] = useState(null);

  // Unified Shop Thresholds Dialog (Phase 2E Enhancement)
  const [unifiedThresholdDialogOpen, setUnifiedThresholdDialogOpen] = useState(false);
  const [unifiedThresholdProduct, setUnifiedThresholdProduct] = useState(null);
  const [unifiedThresholdData, setUnifiedThresholdData] = useState(null);
  const [unifiedThresholdLoading, setUnifiedThresholdLoading] = useState(false);
  const [unifiedThresholdSaving, setUnifiedThresholdSaving] = useState(false);
  const [unifiedThresholdEdits, setUnifiedThresholdEdits] = useState({});  // {shop_id: {min_qty, max_qty, clear}}

  // Filter options
  const [brandOptions, setBrandOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);

  // Fetch filter options
  useEffect(() => {
    api.get("/products/filters").then(({ data }) => {
      setBrandOptions((data.brands || []).map(b => b.name).filter(Boolean));
      setGroupOptions((data.groups || []).map(g => g.name).filter(Boolean));
    }).catch(() => {});
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await api.get("/stock/rebalancer/stats");
      setStats(data);
      // Update default settings from stats if available
      if (data.default_settings) {
        setDefaultSettings({
          normal_min_qty: data.default_settings.normal?.min_qty || 2,
          normal_max_qty: data.default_settings.normal?.max_qty || 10,
          warehouse_min_qty: data.default_settings.warehouse?.min_qty || 5,
          warehouse_max_qty: data.default_settings.warehouse?.max_qty || 30
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch default settings
  const fetchDefaultSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const { data } = await api.get("/stock/rebalancer/default-settings");
      setDefaultSettings(data);
      setEditedSettings(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Save default settings
  const saveDefaultSettings = async () => {
    if (!editedSettings) return;
    
    // Validate
    const { normal_min_qty, normal_max_qty, warehouse_min_qty, warehouse_max_qty } = editedSettings;
    if (normal_min_qty < 0 || warehouse_min_qty < 0) {
      toast.error("Minimum quantities cannot be negative");
      return;
    }
    if (normal_max_qty < normal_min_qty) {
      toast.error("Normal max must be >= normal min");
      return;
    }
    if (warehouse_max_qty < warehouse_min_qty) {
      toast.error("Warehouse max must be >= warehouse min");
      return;
    }
    
    setSettingsSaving(true);
    try {
      await api.put("/stock/rebalancer/default-settings", editedSettings);
      toast.success("Default thresholds updated");
      setDefaultSettings(editedSettings);
      setEditedSettings(null);
      // Refresh data
      fetchStats();
      fetchCrossShop();
      fetchRecommendations();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettingsSaving(false);
    }
  };

  // Fetch cross-shop view
  const fetchCrossShop = useCallback(async () => {
    setCrossShopLoading(true);
    try {
      const params = {
        skip: (crossShopPage - 1) * 20,
        limit: 20,
        q: crossShopSearch,
        brand: crossShopBrand,
        group: crossShopGroup,
        status: crossShopStatus,
        custom_only: crossShopCustomOnly,
      };
      const { data } = await api.get("/stock/rebalancer/cross-shop-view", { params });
      setCrossShopItems(data.items || []);
      setCrossShopTotal(data.total || 0);
      // Update shops with types
      if (data.shops) {
        setShopsWithTypes(data.shops);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCrossShopLoading(false);
    }
  }, [crossShopPage, crossShopSearch, crossShopBrand, crossShopGroup, crossShopStatus, crossShopCustomOnly]);

  // Fetch recommendations with filters
  const fetchRecommendations = useCallback(async () => {
    setRecLoading(true);
    try {
      const params = {};
      if (recBrand) params.brand = recBrand;
      if (recGroup) params.group = recGroup;
      
      // Fetch filtered data
      const { data } = await api.get("/stock/rebalancer/recommendations", { params });
      let recs = data.recommendations || [];
      
      // Client-side search filter (product name, barcode)
      if (recSearch.trim()) {
        const searchLower = recSearch.toLowerCase().trim();
        recs = recs.filter(r => 
          r.product_name?.toLowerCase().includes(searchLower) ||
          r.brand_name?.toLowerCase().includes(searchLower)
        );
      }
      
      // Client-side FROM shop filter (by shop name)
      if (recFromShop) {
        recs = recs.filter(r => r.source_shop_name === recFromShop);
      }
      
      // Client-side TO shop filter (by shop name)
      if (recToShop) {
        recs = recs.filter(r => r.destination_shop_name === recToShop);
      }
      
      setRecommendations(recs);
      setRecFilteredCount(recs.length);
      setRecSummary(data.summary || null);
      
      // Calculate filtered summary based on visible recommendations
      const filteredSummary = {
        total_products_analyzed: data.summary?.total_products_analyzed || 0,
        products_with_recommendations: new Set(recs.map(r => r.product_id)).size,
        total_recommended_transfers: recs.length,
        total_recommended_units: recs.reduce((sum, r) => sum + (r.recommended_qty || 0), 0),
      };
      setRecFilteredSummary(filteredSummary);
      
      // If filters are applied, fetch unfiltered total count
      if (recBrand || recGroup || recSearch || recFromShop || recToShop) {
        try {
          const { data: unfilteredData } = await api.get("/stock/rebalancer/recommendations");
          setRecTotalCount(unfilteredData.summary?.total_recommended_transfers || 0);
        } catch {
          // If unfiltered fetch fails, use filtered count as fallback
          setRecTotalCount(recs.length);
        }
      } else {
        // No filters - total equals filtered
        setRecTotalCount(data.summary?.total_recommended_transfers || 0);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRecLoading(false);
    }
  }, [recSearch, recBrand, recGroup, recFromShop, recToShop]);

  // Fetch thresholds
  const fetchThresholds = useCallback(async () => {
    setThresholdsLoading(true);
    try {
      const { data } = await api.get("/stock/rebalancer/thresholds", {
        params: { limit: 100, q: thresholdSearch }
      });
      setThresholds(data.items || []);
      setThresholdsTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setThresholdsLoading(false);
    }
  }, [thresholdSearch]);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchCrossShop();
    fetchRecommendations();
    fetchDefaultSettings();
  }, [fetchStats, fetchCrossShop, fetchRecommendations, fetchDefaultSettings]);

  // Refresh cross-shop on filter change
  useEffect(() => {
    fetchCrossShop();
  }, [crossShopPage, crossShopSearch, crossShopBrand, crossShopGroup, crossShopStatus, crossShopCustomOnly, fetchCrossShop]);

  // Refresh thresholds on search change
  useEffect(() => {
    if (viewMode === "thresholds") {
      fetchThresholds();
    }
    if (viewMode === "settings") {
      fetchDefaultSettings();
    }
  }, [viewMode, thresholdSearch, fetchThresholds, fetchDefaultSettings]);

  // Debounced recommendation search
  useEffect(() => {
    if (viewMode === "recommendations") {
      const timeout = setTimeout(() => {
        fetchRecommendations();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [viewMode, recSearch, recBrand, recGroup, recFromShop, recToShop, fetchRecommendations]);

  // Clear recommendation filters
  const clearRecFilters = () => {
    setRecSearch("");
    setRecBrand("");
    setRecGroup("");
    setRecFromShop("");
    setRecToShop("");
  };

  // Check if any recommendation filters are active
  const hasRecFilters = recSearch || recBrand || recGroup || recFromShop || recToShop;

  // Product search for threshold dialog
  useEffect(() => {
    if (!productSearchQuery || productSearchQuery.length < 2) {
      setProductSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setProductSearching(true);
      try {
        const { data } = await api.get("/products", { params: { q: productSearchQuery, limit: 10 } });
        setProductSearchResults(data.items || []);
      } catch (e) {
        // Ignore
      } finally {
        setProductSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [productSearchQuery]);

  // Open threshold dialog for create
  const openCreateThreshold = () => {
    setThresholdDialogMode("create");
    setThresholdProduct(null);
    setNormalMinQty("");
    setNormalMaxQty("");
    setWarehouseMinQty("");
    setWarehouseMaxQty("");
    setProductSearchQuery("");
    setProductSearchResults([]);
    setThresholdDialogOpen(true);
  };

  // Open threshold dialog for edit
  const openEditThreshold = (item) => {
    setThresholdDialogMode("edit");
    setThresholdProduct({
      id: item.product_id,
      product: item.product_name,
      brand_name: item.brand_name,
      group_name: item.group_name,
      // Track original values to detect clearing
      had_normal: item.has_normal_threshold,
      had_warehouse: item.has_warehouse_threshold,
    });
    // Set shop-type specific values
    setNormalMinQty(item.normal_min_qty?.toString() || "");
    setNormalMaxQty(item.normal_max_qty?.toString() || "");
    setWarehouseMinQty(item.warehouse_min_qty?.toString() || "");
    setWarehouseMaxQty(item.warehouse_max_qty?.toString() || "");
    setThresholdDialogOpen(true);
  };

  // Select product for threshold
  const selectProductForThreshold = (product) => {
    setThresholdProduct(product);
    setProductSearchQuery("");
    setProductSearchResults([]);
  };

  // Save threshold
  const saveThreshold = async () => {
    if (!thresholdProduct) {
      toast.error("Please select a product");
      return;
    }
    
    // Parse values
    const normalMin = normalMinQty !== "" ? parseInt(normalMinQty, 10) : null;
    const normalMax = normalMaxQty !== "" ? parseInt(normalMaxQty, 10) : null;
    const warehouseMin = warehouseMinQty !== "" ? parseInt(warehouseMinQty, 10) : null;
    const warehouseMax = warehouseMaxQty !== "" ? parseInt(warehouseMaxQty, 10) : null;
    
    const hasNormal = normalMin !== null && normalMax !== null;
    const hasWarehouse = warehouseMin !== null && warehouseMax !== null;
    
    // In edit mode, detect if a shop type is being cleared
    const isEditMode = thresholdDialogMode === "edit";
    const clearNormal = isEditMode && thresholdProduct.had_normal && !hasNormal;
    const clearWarehouse = isEditMode && thresholdProduct.had_warehouse && !hasWarehouse;
    
    // If clearing both (or neither filled), delete the threshold
    if (!hasNormal && !hasWarehouse) {
      if (isEditMode && (thresholdProduct.had_normal || thresholdProduct.had_warehouse)) {
        // Clearing all thresholds - delete the document
        setThresholdSaving(true);
        try {
          await api.delete(`/stock/rebalancer/thresholds/${thresholdProduct.id}`);
          toast.success("Threshold deleted - product will use defaults");
          setThresholdDialogOpen(false);
          fetchThresholds();
          fetchStats();
          fetchCrossShop();
          fetchRecommendations();
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          setThresholdSaving(false);
        }
        return;
      }
      toast.error("Configure thresholds for at least one shop type (Normal or Warehouse)");
      return;
    }
    
    // Validate ranges
    if (hasNormal) {
      if (normalMin < 0 || normalMax < 0) {
        toast.error("Normal shop quantities cannot be negative");
        return;
      }
      if (normalMax < normalMin) {
        toast.error("Normal shop max must be >= min");
        return;
      }
    }
    if (hasWarehouse) {
      if (warehouseMin < 0 || warehouseMax < 0) {
        toast.error("Warehouse quantities cannot be negative");
        return;
      }
      if (warehouseMax < warehouseMin) {
        toast.error("Warehouse max must be >= min");
        return;
      }
    }

    setThresholdSaving(true);
    try {
      // In edit mode with clearing, use PATCH to properly clear shop type
      if (isEditMode && (clearNormal || clearWarehouse)) {
        const patchPayload = {};
        if (clearNormal) {
          patchPayload.clear_normal = true;
        }
        if (clearWarehouse) {
          patchPayload.clear_warehouse = true;
        }
        // Add updated values for non-cleared shop types
        if (hasNormal) {
          patchPayload.normal_min_qty = normalMin;
          patchPayload.normal_max_qty = normalMax;
        }
        if (hasWarehouse) {
          patchPayload.warehouse_min_qty = warehouseMin;
          patchPayload.warehouse_max_qty = warehouseMax;
        }
        await api.patch(`/stock/rebalancer/thresholds/${thresholdProduct.id}`, patchPayload);
        toast.success("Threshold updated successfully");
      } else {
        // Create or update without clearing
        const payload = {
          product_id: thresholdProduct.id,
        };
        if (hasNormal) {
          payload.normal_min_qty = normalMin;
          payload.normal_max_qty = normalMax;
        }
        if (hasWarehouse) {
          payload.warehouse_min_qty = warehouseMin;
          payload.warehouse_max_qty = warehouseMax;
        }
        await api.post("/stock/rebalancer/thresholds", payload);
        toast.success(`Threshold ${thresholdDialogMode === "create" ? "created" : "updated"} successfully`);
      }
      
      setThresholdDialogOpen(false);
      fetchThresholds();
      fetchStats();
      fetchCrossShop();
      fetchRecommendations();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setThresholdSaving(false);
    }
  };

  // Delete threshold
  const deleteThreshold = async (productId) => {
    if (!confirm("Remove threshold configuration for this product?")) return;
    
    try {
      await api.delete(`/stock/rebalancer/thresholds/${productId}`);
      toast.success("Threshold removed");
      fetchThresholds();
      fetchStats();
      fetchCrossShop();
      fetchRecommendations();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Open shop override dialog (Phase 2E)
  const openShopOverrideDialog = (product, shop, existingOverride = null) => {
    setShopOverrideProduct(product);
    setShopOverrideShop(shop);
    setShopOverrideExisting(existingOverride);
    if (existingOverride && existingOverride.threshold_source === "shop_override") {
      setShopOverrideMinQty(String(existingOverride.min_qty || ""));
      setShopOverrideMaxQty(String(existingOverride.max_qty || ""));
    } else {
      setShopOverrideMinQty("");
      setShopOverrideMaxQty("");
    }
    setShopOverrideDialogOpen(true);
  };

  // Save shop override (Phase 2E)
  const saveShopOverride = async () => {
    if (!shopOverrideProduct || !shopOverrideShop) return;
    
    const minQty = parseInt(shopOverrideMinQty, 10);
    const maxQty = parseInt(shopOverrideMaxQty, 10);
    
    if (isNaN(minQty) || isNaN(maxQty)) {
      toast.error("Please enter valid min and max quantities");
      return;
    }
    if (minQty < 0 || maxQty < 0) {
      toast.error("Quantities cannot be negative");
      return;
    }
    if (maxQty < minQty) {
      toast.error("Max must be >= min");
      return;
    }
    
    setShopOverrideSaving(true);
    try {
      await api.post("/stock/rebalancer/shop-overrides", {
        product_id: shopOverrideProduct.product_id,
        shop_id: shopOverrideShop.shop_id,
        min_qty: minQty,
        max_qty: maxQty,
      });
      toast.success(`Shop override saved for ${shopOverrideShop.shop_name}`);
      setShopOverrideDialogOpen(false);
      fetchCrossShop();
      fetchRecommendations();
      fetchStats();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setShopOverrideSaving(false);
    }
  };

  // Delete shop override (Phase 2E)
  const deleteShopOverride = async () => {
    if (!shopOverrideProduct || !shopOverrideShop) return;
    if (!confirm(`Remove shop-specific override for ${shopOverrideShop.shop_name}?`)) return;
    
    setShopOverrideSaving(true);
    try {
      await api.delete(`/stock/rebalancer/shop-overrides/${shopOverrideProduct.product_id}/${shopOverrideShop.shop_id}`);
      toast.success(`Shop override removed for ${shopOverrideShop.shop_name}`);
      setShopOverrideDialogOpen(false);
      fetchCrossShop();
      fetchRecommendations();
      fetchStats();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setShopOverrideSaving(false);
    }
  };

  // ============================================================
  // UNIFIED SHOP THRESHOLDS DIALOG (Phase 2E Enhancement)
  // ============================================================
  
  // Open unified threshold dialog
  const openUnifiedThresholdDialog = async (product) => {
    setUnifiedThresholdProduct(product);
    setUnifiedThresholdEdits({});
    setUnifiedThresholdDialogOpen(true);
    setUnifiedThresholdLoading(true);
    
    try {
      const { data } = await api.get(`/stock/rebalancer/product-all-thresholds/${product.product_id}`);
      setUnifiedThresholdData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setUnifiedThresholdDialogOpen(false);
    } finally {
      setUnifiedThresholdLoading(false);
    }
  };

  // Handle override edit change in unified dialog
  const handleUnifiedThresholdEdit = (shopId, field, value) => {
    setUnifiedThresholdEdits(prev => {
      const current = prev[shopId] || {};
      return {
        ...prev,
        [shopId]: {
          ...current,
          [field]: value,
          clear: false  // If editing, not clearing
        }
      };
    });
  };

  // Handle clear override in unified dialog
  const handleUnifiedThresholdClear = (shopId) => {
    setUnifiedThresholdEdits(prev => ({
      ...prev,
      [shopId]: { clear: true }
    }));
  };

  // Undo clear/edit for a shop
  const handleUnifiedThresholdUndo = (shopId) => {
    setUnifiedThresholdEdits(prev => {
      const newEdits = { ...prev };
      delete newEdits[shopId];
      return newEdits;
    });
  };

  // Save unified threshold edits
  const saveUnifiedThresholds = async () => {
    if (!unifiedThresholdProduct || !unifiedThresholdData) return;
    
    // Build overrides and clears from edits
    const overrides = [];
    const clearShopIds = [];
    const validationErrors = [];
    
    for (const [shopId, edit] of Object.entries(unifiedThresholdEdits)) {
      const shop = unifiedThresholdData.shops.find(s => s.shop_id === shopId);
      const shopName = shop?.shop_name || shopId;
      
      if (edit.clear) {
        // Only clear if there was actually an override
        if (shop?.has_shop_override) {
          clearShopIds.push(shopId);
        }
      } else if (edit.min_qty !== undefined || edit.max_qty !== undefined) {
        // Validate
        const minStr = edit.min_qty !== undefined ? edit.min_qty : "";
        const maxStr = edit.max_qty !== undefined ? edit.max_qty : "";
        
        // If both are empty, skip (no override being set)
        if (minStr === "" && maxStr === "") continue;
        
        // If only one is filled, error
        if ((minStr === "" && maxStr !== "") || (minStr !== "" && maxStr === "")) {
          validationErrors.push(`${shopName}: Both Min and Max required`);
          continue;
        }
        
        const minQty = parseInt(minStr, 10);
        const maxQty = parseInt(maxStr, 10);
        
        if (isNaN(minQty) || isNaN(maxQty)) {
          validationErrors.push(`${shopName}: Invalid numbers`);
          continue;
        }
        if (minQty < 0 || maxQty < 0) {
          validationErrors.push(`${shopName}: Values cannot be negative`);
          continue;
        }
        if (maxQty < minQty) {
          validationErrors.push(`${shopName}: Max must be >= Min`);
          continue;
        }
        
        overrides.push({ shop_id: shopId, min_qty: minQty, max_qty: maxQty });
      }
    }
    
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join("; "));
      return;
    }
    
    if (overrides.length === 0 && clearShopIds.length === 0) {
      toast.info("No changes to save");
      return;
    }
    
    setUnifiedThresholdSaving(true);
    try {
      const { data } = await api.post("/stock/rebalancer/shop-overrides/bulk", {
        product_id: unifiedThresholdProduct.product_id,
        overrides,
        clear_shop_ids: clearShopIds
      });
      
      if (data.ok) {
        const msgs = [];
        if (data.created_count > 0) msgs.push(`${data.created_count} created`);
        if (data.updated_count > 0) msgs.push(`${data.updated_count} updated`);
        if (data.cleared_count > 0) msgs.push(`${data.cleared_count} cleared`);
        toast.success(`Shop overrides saved: ${msgs.join(", ")}`);
        setUnifiedThresholdDialogOpen(false);
        fetchCrossShop();
        fetchRecommendations();
        fetchStats();
      } else {
        toast.error(`Some errors occurred: ${data.error_count} failed`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUnifiedThresholdSaving(false);
    }
  };

  // Check if there are pending edits
  const hasUnifiedThresholdEdits = Object.keys(unifiedThresholdEdits).length > 0;

  // Refresh all data
  const refreshAll = () => {
    fetchStats();
    fetchCrossShop();
    fetchRecommendations();
    fetchDefaultSettings();
    if (viewMode === "thresholds") {
      fetchThresholds();
    }
  };

  return (
    <div className="space-y-4" data-testid="stock-rebalancer">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Smart Stock Rebalancer
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            View cross-shop stock levels and get rebalancing recommendations
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAll}
            disabled={statsLoading || crossShopLoading || recLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${statsLoading || crossShopLoading || recLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {isMasterOrSuper && (
            <Button size="sm" onClick={openCreateThreshold} data-testid="add-threshold-btn">
              <Plus className="w-4 h-4 mr-1" />
              Set Threshold
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={stats} defaultSettings={defaultSettings} />

      {/* View Mode Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            viewMode === "dashboard" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setViewMode("dashboard")}
          data-testid="tab-dashboard"
        >
          Cross-Shop View
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            viewMode === "recommendations" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setViewMode("recommendations")}
          data-testid="tab-recommendations"
        >
          Recommendations ({recommendations.length})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            viewMode === "thresholds" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setViewMode("thresholds")}
          data-testid="tab-thresholds"
        >
          Custom Thresholds
        </button>
        {isMasterOrSuper && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              viewMode === "settings" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setViewMode("settings")}
            data-testid="tab-settings"
          >
            <Sliders className="w-4 h-4 inline mr-1" />
            Default Settings
          </button>
        )}
      </div>

      {/* Dashboard / Cross-Shop View */}
      {viewMode === "dashboard" && (
        <CrossShopView
          items={crossShopItems}
          total={crossShopTotal}
          loading={crossShopLoading}
          page={crossShopPage}
          onPageChange={(p) => setCrossShopPage(p)}
          search={crossShopSearch}
          onSearchChange={(v) => { setCrossShopSearch(v); setCrossShopPage(1); }}
          brand={crossShopBrand}
          onBrandChange={(v) => { setCrossShopBrand(v); setCrossShopPage(1); }}
          group={crossShopGroup}
          onGroupChange={(v) => { setCrossShopGroup(v); setCrossShopPage(1); }}
          status={crossShopStatus}
          onStatusChange={(v) => { setCrossShopStatus(v); setCrossShopPage(1); }}
          customOnly={crossShopCustomOnly}
          onCustomOnlyChange={(v) => { setCrossShopCustomOnly(v); setCrossShopPage(1); }}
          brandOptions={brandOptions}
          groupOptions={groupOptions}
          shopsWithTypes={shopsWithTypes}
          isMasterOrSuper={isMasterOrSuper}
          onOpenUnifiedThresholdDialog={openUnifiedThresholdDialog}
          onOpenShopOverrideDialog={openShopOverrideDialog}
          onOpenEditThreshold={openEditThreshold}
        />
      )}

      {/* Recommendations View */}
      {viewMode === "recommendations" && (
        <RecommendationsView
          recommendations={recommendations}
          summary={recSummary}
          filteredSummary={recFilteredSummary}
          loading={recLoading}
          filteredCount={recFilteredCount}
          totalCount={recTotalCount}
          search={recSearch}
          onSearchChange={setRecSearch}
          brand={recBrand}
          onBrandChange={setRecBrand}
          group={recGroup}
          onGroupChange={setRecGroup}
          fromShop={recFromShop}
          onFromShopChange={(v) => {
            setRecFromShop(v);
            // If user selects same shop as TO, clear TO
            if (v && v === recToShop) {
              setRecToShop("");
            }
          }}
          toShop={recToShop}
          onToShopChange={(v) => {
            setRecToShop(v);
            // If user selects same shop as FROM, clear FROM
            if (v && v === recFromShop) {
              setRecFromShop("");
            }
          }}
          brandOptions={brandOptions}
          groupOptions={groupOptions}
          shopsWithTypes={shopsWithTypes}
          onRefresh={fetchRecommendations}
          onClearFilters={clearRecFilters}
          onCreateTransfer={onCreateTransfer}
        />
      )}

      {/* Threshold Settings View */}
      {viewMode === "thresholds" && (
        <div className="space-y-4">
          {/* Search */}
          <Card className="p-3">
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-xs">Search Products with Thresholds</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by product name..."
                    value={thresholdSearch}
                    onChange={(e) => setThresholdSearch(e.target.value)}
                    className="pl-8 h-9"
                  />
                </div>
              </div>
              {isMasterOrSuper && (
                <Button onClick={openCreateThreshold}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Threshold
                </Button>
              )}
            </div>
          </Card>

          {/* Thresholds Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-center w-[140px]">
                    <div className="flex items-center justify-center gap-1">
                      <Building2 className="w-3 h-3" />
                      Normal
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[140px]">
                    <div className="flex items-center justify-center gap-1">
                      <Warehouse className="w-3 h-3" />
                      Warehouse
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[120px]">Updated</TableHead>
                  {isMasterOrSuper && <TableHead className="text-center w-[80px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {thresholdsLoading ? (
                  <TableRow>
                    <TableCell colSpan={isMasterOrSuper ? 5 : 4} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : thresholds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMasterOrSuper ? 5 : 4} className="text-center py-8 text-muted-foreground">
                      No thresholds configured yet
                    </TableCell>
                  </TableRow>
                ) : (
                  thresholds.map(t => (
                    <TableRow key={t.product_id} data-testid={`threshold-row-${t.product_id}`}>
                      <TableCell>
                        <div className="font-medium text-sm">{t.product_name}</div>
                        <div className="text-xs text-muted-foreground">{t.brand_name} · {t.group_name}</div>
                      </TableCell>
                      <TableCell className="text-center">
                        {t.has_normal_threshold ? (
                          <div className="text-xs">
                            <span className="font-mono">{t.normal_min_qty} - {t.normal_max_qty}</span>
                            <Badge variant="outline" className="ml-1 text-[9px] bg-slate-100">NOR</Badge>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {t.has_warehouse_threshold ? (
                          <div className="text-xs">
                            <span className="font-mono">{t.warehouse_min_qty} - {t.warehouse_max_qty}</span>
                            <Badge variant="outline" className="ml-1 text-[9px] bg-blue-100">WH</Badge>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-xs text-muted-foreground">
                        {new Date(t.updated_at).toLocaleDateString()}
                      </TableCell>
                      {isMasterOrSuper && (
                        <TableCell className="text-center">
                          <div className="flex gap-1 justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => openEditThreshold(t)}
                              data-testid={`edit-threshold-${t.product_id}`}
                            >
                              <Settings className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-red-600 hover:text-red-700"
                              onClick={() => deleteThreshold(t.product_id)}
                              data-testid={`delete-threshold-${t.product_id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Default Settings View */}
      {viewMode === "settings" && (
        <div className="space-y-4">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium flex items-center gap-2 mb-2">
                  <Sliders className="w-5 h-5" />
                  Default Threshold Settings
                </h3>
                <p className="text-sm text-muted-foreground">
                  Set default min/max stock levels by shop type. Products without custom thresholds will use these defaults.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Normal Shop Defaults */}
                <div className="p-4 rounded-lg border bg-slate-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-slate-600" />
                    <h4 className="font-medium">Normal Shop Defaults</h4>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Regular retail shops with standard stock requirements.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Minimum Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        value={editedSettings?.normal_min_qty ?? defaultSettings.normal_min_qty}
                        onChange={(e) => setEditedSettings({
                          ...editedSettings || defaultSettings,
                          normal_min_qty: parseInt(e.target.value) || 0
                        })}
                        className="mt-1"
                        data-testid="setting-normal-min"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Below = LOW STOCK</p>
                    </div>
                    <div>
                      <Label className="text-xs">Maximum Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        value={editedSettings?.normal_max_qty ?? defaultSettings.normal_max_qty}
                        onChange={(e) => setEditedSettings({
                          ...editedSettings || defaultSettings,
                          normal_max_qty: parseInt(e.target.value) || 0
                        })}
                        className="mt-1"
                        data-testid="setting-normal-max"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Above = OVERSTOCK</p>
                    </div>
                  </div>
                </div>

                {/* Warehouse Defaults */}
                <div className="p-4 rounded-lg border bg-blue-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Warehouse className="w-5 h-5 text-blue-600" />
                    <h4 className="font-medium">Warehouse Defaults</h4>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Larger shops that also function as stock-holding locations.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Minimum Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        value={editedSettings?.warehouse_min_qty ?? defaultSettings.warehouse_min_qty}
                        onChange={(e) => setEditedSettings({
                          ...editedSettings || defaultSettings,
                          warehouse_min_qty: parseInt(e.target.value) || 0
                        })}
                        className="mt-1"
                        data-testid="setting-warehouse-min"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Below = LOW STOCK</p>
                    </div>
                    <div>
                      <Label className="text-xs">Maximum Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        value={editedSettings?.warehouse_max_qty ?? defaultSettings.warehouse_max_qty}
                        onChange={(e) => setEditedSettings({
                          ...editedSettings || defaultSettings,
                          warehouse_max_qty: parseInt(e.target.value) || 0
                        })}
                        className="mt-1"
                        data-testid="setting-warehouse-max"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Above = OVERSTOCK</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              {editedSettings && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setEditedSettings(null)}
                    disabled={settingsSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveDefaultSettings}
                    disabled={settingsSaving}
                    data-testid="save-default-settings-btn"
                  >
                    {settingsSaving ? "Saving..." : "Save Default Settings"}
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Info Card */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-blue-800">
                <strong>Threshold Priority Logic:</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside text-xs">
                  <li><strong>Normal Shop:</strong> Custom Normal threshold → Normal Default</li>
                  <li><strong>Warehouse:</strong> Custom Warehouse threshold → Warehouse Default</li>
                  <li>A Normal shop custom threshold does <strong>NOT</strong> affect Warehouses</li>
                  <li>A Warehouse custom threshold does <strong>NOT</strong> affect Normal shops</li>
                  <li>Products can have custom thresholds for Normal only, Warehouse only, or both</li>
                  <li>Set shop types in <strong>Settings → Shop Settings</strong></li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Threshold Dialog - uses extracted component */}
      <ThresholdDialog
        open={thresholdDialogOpen}
        onOpenChange={setThresholdDialogOpen}
        mode={thresholdDialogMode}
        product={thresholdProduct}
        onProductSelect={selectProductForThreshold}
        onProductClear={() => setThresholdProduct(null)}
        searchQuery={productSearchQuery}
        onSearchChange={setProductSearchQuery}
        searching={productSearching}
        searchResults={productSearchResults}
        normalMinQty={normalMinQty}
        normalMaxQty={normalMaxQty}
        warehouseMinQty={warehouseMinQty}
        warehouseMaxQty={warehouseMaxQty}
        onNormalMinChange={setNormalMinQty}
        onNormalMaxChange={setNormalMaxQty}
        onWarehouseMinChange={setWarehouseMinQty}
        onWarehouseMaxChange={setWarehouseMaxQty}
        onClearNormal={() => { setNormalMinQty(""); setNormalMaxQty(""); }}
        onClearWarehouse={() => { setWarehouseMinQty(""); setWarehouseMaxQty(""); }}
        saving={thresholdSaving}
        onSave={saveThreshold}
      />

      {/* Shop Override Dialog - uses extracted component */}
      <ShopOverrideDialog
        open={shopOverrideDialogOpen}
        onOpenChange={setShopOverrideDialogOpen}
        product={shopOverrideProduct}
        shop={shopOverrideShop}
        existingOverride={shopOverrideExisting}
        minQty={shopOverrideMinQty}
        maxQty={shopOverrideMaxQty}
        onMinChange={setShopOverrideMinQty}
        onMaxChange={setShopOverrideMaxQty}
        saving={shopOverrideSaving}
        onSave={saveShopOverride}
        onDelete={deleteShopOverride}
      />

      {/* Unified Shop Thresholds Dialog - uses extracted component */}
      <UnifiedThresholdDialog
        open={unifiedThresholdDialogOpen}
        onOpenChange={(open) => {
          if (!open && hasUnifiedThresholdEdits) {
            if (!confirm("You have unsaved changes. Discard them?")) return;
          }
          setUnifiedThresholdDialogOpen(open);
          if (!open) {
            setUnifiedThresholdEdits({});
            setUnifiedThresholdData(null);
          }
        }}
        product={unifiedThresholdProduct}
        data={unifiedThresholdData}
        loading={unifiedThresholdLoading}
        saving={unifiedThresholdSaving}
        edits={unifiedThresholdEdits}
        onEdit={handleUnifiedThresholdEdit}
        onClear={handleUnifiedThresholdClear}
        onUndo={handleUnifiedThresholdUndo}
        onSave={saveUnifiedThresholds}
      />
    </div>
  );
}
